"use client";

/**
 * ShareButton 컴포넌트
 * @description 헤더 오른쪽의 공유 버튼. 공개 페이지(콜랩 · 글)의 목적이 확산이라 공유는 첫
 *   화면에 있어야 한다. 기기가 공유 시트를 주면 그것을, 아니면 링크를 복사하고 잠깐
 *   "Link copied" 를 보인다. 배경이 늘 화면 바탕이라 DS 의 ghost IconButton 이다.
 *   어디서 눌렸는지의 계측은 호출부가 `onShared` 로 한다.
 * @example
 * <Header inset="screen" trailingGhost><HeaderBrandSlot /><ShareButton title={post.title} /></Header>
 */
import { IconButton } from "@fanwork-web/design-system";
import { Share } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { shareButtonStyles } from "./ShareButton.styles";
import type { ShareButtonProps } from "./ShareButton.types";

import { SHARE_COPIED_MS, shareCurrentUrl } from "@/shared/lib/share";

const COPY = { label: "Share", copied: "Link copied" } as const;

const ShareButton = memo<ShareButtonProps>(
  ({ title, onShared, label = COPY.label, copiedLabel = COPY.copied }) => {
    const [isCopied, setCopied] = useState(false);

    const share = useCallback(async () => {
      const outcome = await shareCurrentUrl(title);
      onShared?.(outcome);
      if (outcome !== "copied") return;
      setCopied(true);
      window.setTimeout(() => setCopied(false), SHARE_COPIED_MS);
    }, [onShared, title]);

    return (
      <div className={shareButtonStyles.root}>
        {isCopied ? (
          <span role="status" className={shareButtonStyles.copied}>
            {copiedLabel}
          </span>
        ) : null}
        <IconButton
          icon={<Share aria-hidden />}
          label={label}
          tone="ghost"
          radius="circle"
          onClick={share}
        />
      </div>
    );
  },
);

ShareButton.displayName = "ShareButton";

export default ShareButton;
